import { IconExternalLink, IconPackage } from "@tabler/icons-react";
import type { BadgeState } from "./badge";
import { Badge } from "./badge";
import { AppCard } from "./card";
import { Button } from "./button";
import { Text } from "./typography";
import { formatDate } from "../../utils/date";
import { dollarFormat } from "../../utils/currencies";
import type { DropshippingDto } from "../../api";

type ShipmentWithInternalDeliveryCost = DropshippingDto & {
  internalDeliveryCostEstimate?: number | null;
  internalDeliveryCostCurrency?: string | null;
  internalDeliveryActualCost?: number | null;
  internalDeliveryActualCostCurrency?: string | null;
  internalDeliveryCostSource?: string | null;
  internalDeliveryCostRecordedAt?: string | null;
};

interface OrderShipmentsCardProps {
  shipments?: ShipmentWithInternalDeliveryCost[] | null;
  title?: string;
  className?: string;
  showInternalCosts?: boolean;
}

function extractTrackingUrl(note?: string | null): string {
  if (!note) return "";
  const cleaned = note.replace(/<[^>]+>/g, "").trim();
  const match = cleaned.match(/https?:\/\/[^\s<>"']+/i);
  return match?.[0] ?? "";
}

function getShipmentStatus(shipment: DropshippingDto): { label: string; state: BadgeState } {
  const state = (shipment.state ?? "").toLowerCase();

  if (shipment.confirmedByBuyer) {
    return { label: "Received", state: "success" };
  }

  if (state === "done" || state === "delivered") {
    return { label: "Delivered", state: "success" };
  }

  if (state === "assigned" || state === "confirmed" || state === "in_transit" || state === "shipping") {
    return { label: "In transit", state: "pending" };
  }

  if (state === "draft" || state === "planned" || state === "pending") {
    return { label: "Pending", state: "pending" };
  }

  if (state === "cancelled" || state === "canceled") {
    return { label: "Cancelled", state: "error" };
  }

  if (!state) {
    return { label: "Scheduled", state: "info" };
  }

  return {
    label: state.charAt(0).toUpperCase() + state.slice(1),
    state: "info",
  };
}

function ShipmentRow({
  shipment,
  index,
  showInternalCosts = false,
}: {
  shipment: ShipmentWithInternalDeliveryCost;
  index: number;
  showInternalCosts?: boolean;
}) {
  const trackingUrl = (shipment as unknown as { deliveryTrackingUrl?: string }).deliveryTrackingUrl
    || extractTrackingUrl(shipment.note);
  const eta = shipment.scheduledDate ?? shipment.dateDeadline ?? null;
  const status = getShipmentStatus(shipment);
  const internalCost = shipment.internalDeliveryCostEstimate ?? null;
  const internalCurrency = shipment.internalDeliveryCostCurrency ?? "QAR";
  const actualCost = shipment.internalDeliveryActualCost ?? null;
  const actualCurrency = shipment.internalDeliveryActualCostCurrency ?? internalCurrency;
  const costSource = shipment.internalDeliveryCostSource ?? null;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-borderDefault bg-bgWhite p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-borderDefault bg-bgLight">
            <IconPackage size={18} className="text-textDark" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark">
              Shipment {index + 1}
            </Text>
            <Text variant="bodySm" tone="muted">
              Order-level parcel tracking
            </Text>
          </div>
        </div>
        <Badge label={status.label} state={status.state} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {eta && (
          <div className="flex items-center gap-2">
            <Text variant="bodySm" tone="body">Expected</Text>
            <Text variant="bodySm" tone="dark">{formatDate(eta)}</Text>
          </div>
        )}
        {shipment.dateDeadline && shipment.scheduledDate && shipment.dateDeadline !== shipment.scheduledDate && (
          <div className="flex items-center gap-2">
            <Text variant="bodySm" tone="body">Deadline</Text>
            <Text variant="bodySm" tone="dark">{formatDate(shipment.dateDeadline)}</Text>
          </div>
        )}
        {shipment.confirmedByBuyer && (
          <div className="flex items-center gap-2">
            <Text variant="bodySm" tone="body">Buyer status</Text>
            <Text variant="bodySm" tone="dark">Confirmed</Text>
          </div>
        )}
      </div>

      {showInternalCosts && internalCost != null && (
        <div className="rounded-lg border border-dashed border-borderDefault bg-bgLight px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <Text variant="bodySm" tone="muted">Internal delivery estimate</Text>
            <Text variant="bodySmBold" tone="dark">{dollarFormat(internalCost, internalCurrency)}</Text>
          </div>
          {actualCost != null && (
            <div className="mt-1 flex items-center justify-between gap-3">
              <Text variant="bodySm" tone="muted">Actual recorded cost</Text>
              <Text variant="bodySmBold" tone="dark">{dollarFormat(actualCost, actualCurrency)}</Text>
            </div>
          )}
          {costSource && (
            <Text variant="bodyXs" tone="muted" className="mt-1">
              Source: {costSource}
            </Text>
          )}
        </div>
      )}

      {trackingUrl && (
        <div>
          <Button
            variant="ghostAccent"
            size="sm"
            leftIcon={<IconExternalLink size={14} />}
            className="-ml-2"
            onClick={() => window.open(trackingUrl, "_blank", "noopener,noreferrer")}
          >
            Open tracking link
          </Button>
        </div>
      )}
    </div>
  );
}

export function OrderShipmentsCard({
  shipments,
  title = "Shipments",
  className = "",
  showInternalCosts = false,
}: OrderShipmentsCardProps) {
  if (!shipments || shipments.length === 0) {
    return null;
  }

  const totalInternalCost = showInternalCosts
    ? shipments.reduce((sum, shipment) => sum + Number(shipment.internalDeliveryCostEstimate ?? 0), 0)
    : 0;
  const totalActualInternalCost = showInternalCosts
    ? shipments.reduce((sum, shipment) => sum + Number(shipment.internalDeliveryActualCost ?? 0), 0)
    : 0;
  const internalCurrency = shipments.find((shipment) => shipment.internalDeliveryCostCurrency)?.internalDeliveryCostCurrency ?? "QAR";
  const actualCurrency = shipments.find((shipment) => shipment.internalDeliveryActualCostCurrency)?.internalDeliveryActualCostCurrency
    ?? shipments.find((shipment) => shipment.internalDeliveryCostCurrency)?.internalDeliveryCostCurrency
    ?? "QAR";

  return (
    <AppCard
      title={title}
      color="bg-bgWhite"
      borderColor="border-borderDefault"
      className={`w-full ${className}`}
    >
      <div className="flex flex-col gap-4">
        {showInternalCosts && (
          <div className="rounded-xl border border-dashed border-borderDefault bg-bgLight px-4 py-3">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-3">
                <Text variant="bodySm" tone="muted">
                  Internal delivery estimate
                </Text>
                <Text variant="bodySmBold" tone="dark">
                  {dollarFormat(totalInternalCost, internalCurrency)}
                </Text>
              </div>
              <div className="flex items-center justify-between gap-3">
                <Text variant="bodySm" tone="muted">
                  Actual recorded delivery
                </Text>
                <Text variant="bodySmBold" tone="dark">
                  {dollarFormat(totalActualInternalCost, actualCurrency)}
                </Text>
              </div>
            </div>
            <Text variant="bodyXs" tone="muted" className="mt-1">
              Procurtal-owned delivery cost tracking only. Not shown to buyers at checkout.
            </Text>
          </div>
        )}
        {shipments.length > 1 && (
          <div className="rounded-xl border border-dashed border-borderDefault bg-bgLight px-4 py-3">
            <Text variant="bodySm" tone="muted">
              This order may arrive in multiple parcels. Each shipment is shown below at the order level.
            </Text>
          </div>
        )}
        <div className="flex flex-col gap-3">
          {shipments.map((shipment, index) => (
            <ShipmentRow key={shipment.id} shipment={shipment} index={index} showInternalCosts={showInternalCosts} />
          ))}
        </div>
      </div>
    </AppCard>
  );
}
